import React, { useState, useContext, useEffect } from "react";
import { Button, Dropdown, Menu, Modal, Table, Tag, message } from "antd";
import {
	UnorderedListOutlined,
	CaretDownOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import { useListarSolicitudesAusenciaPorJefeInmediato } from "../../hooks";
import moment from "moment";
import { useAtom } from "jotai";
import {
	solicitudAusenciaSeleccionadaAtom,
	visibleModalAprobarSolicitudAusenciaAtom,
	visibleModalRechazarSolicitudAusenciaAtom,
	visibleModalVerDocumentoAdjuntoAtom,
	urlDocumentoAdjuntoAtom,
	visibleModalAprobarMasivaSolicitudAusenciaAtom,
	solicitudesAusenciaSeleccionadasAtom,
} from "../../SolicitudesPendientesAprobarStore";

import { SecurityContext } from "context/SecurityContextProvider";
import { useModalVisorPdf } from "store/global";
import { SolicitudAusenciaService } from "services";
import { Plataforma } from "enum/Plataforma";
import { useBloqueoMovimientos, AvisoBloqueoMovimientos } from "../../../../../common/bloqueo-movimientos";

const esDescansoMedicoTipo = (tipo) =>
	tipo?.descripcion?.toLowerCase().includes("descanso m") ?? false;

const ListaSolicitudesPendientesAprobacion = () => {
	const { solicitudesAusencia } = useListarSolicitudesAusenciaPorJefeInmediato("PENDIENTE");
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isMaccaferriDePeru = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20418710757" ? true : false;
	const isMaccaferriConstruction = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20521749378" ? true : false;
	const isAceros = getUsuarioAutenticado().empresaSeleccionada.razonSocial.toUpperCase().includes("ACEROS CHILCA SAC")
		? true
		: false;
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;
	const habilitarAprobacionMasiva = isSDF || isSDFEnergia;
	const { fechaLimite, estaBloqueada } = useBloqueoMovimientos();

	const [solicitudesSeleccionadas, setSolicitudesSeleccionadas] = useAtom(solicitudesAusenciaSeleccionadasAtom);
	const [, setVisibleModalAprobarMasiva] = useAtom(visibleModalAprobarMasivaSolicitudAusenciaAtom);
	const selectedRowKeys = solicitudesSeleccionadas.map((s) => s.id);

	const [tiposEnfermedad, setTiposEnfermedad] = useState([]);

	useEffect(() => {
		if (isSDF || isSDFEnergia) {
			SolicitudAusenciaService.listarTiposEnfermedad()
				.then((response) => {
					if (response.estado === "EXITO") {
						setTiposEnfermedad(response.data);
					}
				})
				.catch(() => {});
		}
	}, []);

	const getTipoEnfermedadDescripcion = (id) => {
		const tipo = tiposEnfermedad.find((t) => t.id === id);
		return tipo ? tipo.descripcion : id;
	};

	const onSelectionChange = (newSelectedRowKeys, selectedRows) => {
		setSolicitudesSeleccionadas(selectedRows);
	};

	const rowSelection = habilitarAprobacionMasiva
		? {
			selectedRowKeys,
			onChange: onSelectionChange,
			// Las solicitudes dentro del periodo bloqueado no se pueden aprobar (solo rechazar)
			getCheckboxProps: (fila) => ({ disabled: estaBloqueada(fila.fechaInicioAusencia) }),
		}
		: undefined;

	const onClickAprobarSeleccionados = () => {
		const dmSinTipo = solicitudesSeleccionadas.filter(
			(s) => esDescansoMedicoTipo(s.tipo) && !s.tipoEnfermedad
		);
		if (dmSinTipo.length > 0) {
			Modal.warning({
				title: `${dmSinTipo.length} solicitud(es) sin tipo de enfermedad`,
				content: (
					<div>
						<p style={{ marginBottom: 8 }}>
							Las siguientes solicitudes de <strong>Descanso Médico</strong> no pueden aprobarse
							hasta registrar el tipo de enfermedad. Use <em>Ver Solicitud</em> para completarlo.
						</p>
						<ul style={{ maxHeight: 200, overflowY: "auto", paddingLeft: 20, margin: 0 }}>
							{dmSinTipo.map((s) => (
								<li key={s.id}>{s.colaborador.nombreCompleto}</li>
							))}
						</ul>
					</div>
				),
				okText: "Entendido",
			});
			return;
		}
		setVisibleModalAprobarMasiva(true);
	};

	const columnaTipoEnfermedad = {
		title: "Tipo Enfermedad",
		width: "13%",
		align: "center",
		render: (text, fila) => {
			if (!esDescansoMedicoTipo(fila.tipo)) return "-";
			if (!fila.tipoEnfermedad) return <Tag color="red">Sin definir</Tag>;
			return <Tag color="green">{getTipoEnfermedadDescripcion(fila.tipoEnfermedad)}</Tag>;
		},
	};

	const columnas = [
		{
			title: "Colaborador",
			dataIndex: ["colaborador", "nombreCompleto"],
			width: "20%",
		},
		{
			title: (isSDF || isSDFEnergia) ? "Tipo Movimiento": "Tipo Ausencia",
			dataIndex: ["tipo", "descripcion"],
			width: "15%",
			align: "center",
		},
		{
			title: "Fecha Inicio",
			dataIndex: "fechaInicioAusencia",
			width: "13%",
			align: "center",
			render: (text, fila) => {
				const formato =
						(isSDF || isSDFEnergia)
						? fila?.tipo?.contabilizarHoras
							? "DD/MM/YYYY hh:mm a"
							: "DD/MM/YYYY"
						: "DD/MM/YYYY hh:mm a";
				return moment(fila.fechaInicioAusencia).format(formato);
			}
		},
		{
			title: "Fecha Fin",
			dataIndex: "fechaFin",
			width: "13%",
			align: "center",
			render: (text, fila) => {
				const formato =
						(isSDF || isSDFEnergia)
						? fila?.tipo?.contabilizarHoras
							? "DD/MM/YYYY hh:mm a"
							: "DD/MM/YYYY"
						: "DD/MM/YYYY hh:mm a";
				return moment(fila.fechaFinAusencia).format(formato);
			}
		},
		{
			title: "Dias",
			dataIndex: "diasSolicitados",
			width: "5%",
			align: "center",
		},
		{
			title: "Fecha de Solicitud",
			dataIndex: "fechaHoraSolicitud",
			width: "13%",
			align: "center",
			render: (text, fila) => moment(fila.fechaHoraSolicitud).format("DD/MM/YYYY hh:mm a"),
		},
		...((isSDF || isSDFEnergia) ? [columnaTipoEnfermedad] : []),
		{
			title: "Acciones",
			align: "center",
			width: "13%",
			render: (text, fila) => <MenuAcciones solicitud={fila} bloqueada={estaBloqueada(fila.fechaInicioAusencia)} />,
		},
	];

	return (
		<>
			<AvisoBloqueoMovimientos fechaLimite={fechaLimite} accion="aprobar" />
			{habilitarAprobacionMasiva && selectedRowKeys.length > 0 && (
				<div style={{ marginBottom: 8 }}>
					<Button
						type="primary"
						icon={<CheckCircleOutlined />}
						onClick={onClickAprobarSeleccionados}
					>
						Aprobar seleccionados ({selectedRowKeys.length})
					</Button>
				</div>
			)}
			<Table
				rowKey={(value) => value.id}
				rowSelection={rowSelection}
				columns={columnas}
				dataSource={solicitudesAusencia}
				expandable={{
					expandedRowRender: (fila) => <Comentario registro={fila} />,
					rowExpandable: (fila) =>
						((isMaccaferriConstruction || isMaccaferriDePeru) &&
							fila.comentario !== null &&
							fila.comentario !== undefined &&
							fila.comentario !== "") ||
						(isAceros && fila.informacionAdicional !== null)
						|| ((isSDF || isSDFEnergia) && (
						(fila.comentario !== null && fila.comentario !== undefined && fila.comentario !== "")
						|| fila.informacionAdicional !== null)),
				}}
			/>
		</>
	);
};

const MenuAcciones = ({ solicitud, bloqueada }) => {
	const [visibleMenu, setVisibleMenu] = useState(false);
	const [, setSolicituduAusenciaSeleccionada] = useAtom(solicitudAusenciaSeleccionadaAtom);
	const [, setVisibleModalAprobarSolicitudAusencia] = useAtom(visibleModalAprobarSolicitudAusenciaAtom);
	const [, setVisibleModalRechazarSolicitudAusencia] = useAtom(visibleModalRechazarSolicitudAusenciaAtom);
	const [, setVisibleModalVerDocumentoAdjunto] = useAtom(visibleModalVerDocumentoAdjuntoAtom);
	const [, setUrlDocumento] = useAtom(urlDocumentoAdjuntoAtom);
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const mostrarModalVisorPdf = useModalVisorPdf((state) => state.mostrarModal);

	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684";
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090";

	const onClickMenu = async ({ key }) => {
		if (key === "aprobar") {
			onClickOpcionAprobar();
		} else if (key === "rechazar") {
			onClickOpcionRechazar();
		} else if (key === "verSolicitud") {
			onclickVerSolicitud();
		}
	};

	const onClickOpcionAprobar = () => {
		if (bloqueada) {
			message.warning("La fecha de inicio de esta solicitud está dentro del periodo bloqueado. Solo puede rechazarla.");
			return;
		}
		if ((isSDF || isSDFEnergia) && esDescansoMedicoTipo(solicitud.tipo) && !solicitud.tipoEnfermedad) {
			message.warning("Debe registrar el tipo de enfermedad antes de aprobar. Use 'Ver Solicitud' para registrarlo.");
			return;
		}
		setVisibleModalAprobarSolicitudAusencia(true);
		setSolicituduAusenciaSeleccionada(solicitud);
		setVisibleMenu(false);
	};

	const onClickOpcionRechazar = () => {
		setVisibleModalRechazarSolicitudAusencia(true);
		setSolicituduAusenciaSeleccionada(solicitud);
		setVisibleMenu(false);
	};
	const onclickVerSolicitud = () => {
		console.log("ver solicitud");
		setSolicituduAusenciaSeleccionada(solicitud);
		setUrlDocumento("");
		setVisibleModalVerDocumentoAdjunto(true);
	};

	const menu = () => {
		return (
			<Menu onClick={onClickMenu}>
				<Menu.Item key="aprobar" disabled={bloqueada}>
					<CheckCircleOutlined /> Aprobar Solicitud{bloqueada ? " (periodo bloqueado)" : ""}
				</Menu.Item>
				<Menu.Item key="rechazar">
					<CloseCircleOutlined /> Rechazar Solicitud
				</Menu.Item>
				{solicitud?.adjuntos && (
					<Menu.Item key="verSolicitud">
						<SearchOutlined /> Ver Solicitud
					</Menu.Item>
				)}
			</Menu>
		);
	};

	return (
		<React.Fragment>
			<Dropdown
				overlay={menu}
				trigger={["click"]}
				placement="bottomCenter"
				onVisibleChange={(flag) => setVisibleMenu(flag)}
				visible={visibleMenu}
			>
				<a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
					<UnorderedListOutlined style={{ fontSize: "16px", fontWeight: "bold", color: "#1678c1" }} />
					<CaretDownOutlined
						style={{
							paddingLeft: "2px",
							fontSize: "12px",
							fontWeight: "bold",
							color: "#1678c1",
						}}
					/>
				</a>
			</Dropdown>
		</React.Fragment>
	);
};

const Comentario = ({ registro }) => {
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isMaccaferriDePeru = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20418710757" ? true : false;
	const isMaccaferriConstruction = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20521749378" ? true : false;
	const isAceros = getUsuarioAutenticado().empresaSeleccionada.razonSocial.toUpperCase().includes("ACEROS CHILCA SAC")
		? true
		: false;
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;

	return (
		<React.Fragment>
			{(isMaccaferriDePeru || isMaccaferriConstruction) && (
				<p style={{ margin: 5 }}>{"EMPLEADO DESIGNADO: " + registro.comentario}</p>
			)}
			{isAceros && registro.informacionAdicional && (
				<div style={{ marginLeft: 50 }}>
					{Object.entries(registro.informacionAdicional).map(([key, value], index) => (
					<p key={index}>
							<strong>{key.replace(/([A-Z])/g, ' $1').toUpperCase()}: </strong>
							{typeof value === 'boolean'
								? (value ? 'Sí' : 'No')
								: typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/) && moment(value).isValid()
								? moment(value).format("DD/MM/YYYY")
								: value}
					</p>
					))
					}
					<p>
						<strong>Comentario: </strong> {registro.comentario}
					</p>
				</div>
			)}
			{(isSDF || isSDFEnergia) && (
				<>
					{registro.informacionAdicional ? (
						<div style={{ marginLeft: 50 }}>
							{Object.entries(registro.informacionAdicional).map(([key, value], index) => {
								let displayKey = key;
								if (key === 'En-El-Turno-1') {
									displayKey = 'EN- EL- TURNO-SOLICITADO';
								} else if (key === 'En-El-Turno-2') {
									displayKey = 'EN- EL- TURNO-ORIGINAL';
								}

								return (
									<p key={index}>
										<strong>{displayKey.replace(/([A-Z])/g, " $1").toUpperCase()}: </strong>
										{typeof value === "boolean"
											? value ? "Sí" : "No"
											: typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/) && moment(value).isValid()
											? moment(value).format("DD/MM/YYYY")
											: value}
									</p>
								);
							})}
							<p>
								<strong>Comentario: </strong> {registro.comentario}
							</p>
						</div>
					) : (
						<p>
							<strong>Comentario: </strong> {registro.comentario}
						</p>
					)}
				</>
			)}
		</React.Fragment>
	);
};
export default ListaSolicitudesPendientesAprobacion;
